import {
  type ConnectionStringsDto,
  type InstallationConfigDto,
  type UpdateConnectionStringsRequestDto,
  UpdateConnectionStringsRequestDtoTypeEnum,
} from '@/api/generator';
import { CustomValidateFunction } from '@/components/forms/types.ts';
import { AggregatedAjvError } from '@/validation/createSchemaValidator';
import { t } from 'i18next';
import { PartialDeep } from 'type-fest';

const maskedPassword = '••••••••';

export type ConnectionStringParams = Partial<
  Pick<
    ConnectionStringsDto,
    'server' | 'port' | 'database' | 'user' | 'password' | 'additionalParameters'
  >
>;
// Constants
const standardParams = [
  'server',
  'data source',
  'addr',
  'network address',
  'address',
  'database',
  'initial catalog',
  'user id',
  'uid',
  'user',
  'password',
  'pwd',
];
const forbiddenParams = [
  'server',
  'data source',
  'addr',
  'network address',
  'address',
  'database',
  'initial catalog',
  'user id',
  'uid',
  'user',
  'password',
  'pwd',
  'trust server certificate',
];
const trustCertPattern = /(?:^|;)\s*trust\s{1,3}server\s{1,3}certificate\s*=\s*true\s*(?:;|$)/gi;

function getParametersFromConnectionString(mssqlConnectionString: string): ConnectionStringsDto {
  const serverExpression =
    /(?:server|data source|addr|network address|address)\s*=\s*(?<hostAndPort>[^;]+)/i;
  const databaseExpression = /(?:database|initial catalog)\s*=\s*(?<database>[^;]+)/i;
  const userIdExpression = /(?:user\s*id|uid|user)\s*=\s*(?<userId>[^;]+)/i;
  const passwordExpression = /(?:password|pwd)\s*=\s*'(?<password>[^']*)'/i;
  const passwordExpressionNoQuotes = /(?:password|pwd)\s*=\s*(?<password>[^;]+)/i;

  const serverMatch = serverExpression.exec(mssqlConnectionString);
  const databaseMatch = databaseExpression.exec(mssqlConnectionString);
  const userMatch = userIdExpression.exec(mssqlConnectionString);
  const passwordMatch =
    passwordExpression.exec(mssqlConnectionString) ||
    passwordExpressionNoQuotes.exec(mssqlConnectionString);

  // Extract server and port
  let server = '';
  let port: number | undefined;
  const hostAndPort = serverMatch?.groups?.hostAndPort;

  if (hostAndPort) {
    const serverPart = hostAndPort.trim();
    const serverPortMatch = RegExp(/^(?<host>.+?),(?<port>\d+)$/).exec(serverPart);
    if (serverPortMatch) {
      const host = serverPortMatch.groups?.host;
      const portString = serverPortMatch.groups?.port;
      server = host ?? '';
      port = portString ? parseInt(portString, 10) : undefined;
    } else {
      server = serverPart;
    }
  }

  // Extract database, user, and password
  const database = databaseMatch?.groups?.database?.trim() ?? '';
  const userId = userMatch?.groups?.userId?.trim() ?? '';
  const password = passwordMatch?.groups?.password?.trim() ?? '';

  // Extract additional parameters (everything except standard params and Trust Server Certificate)
  let additionalParameters: string | undefined;

  // Remove "Trust Server Certificate" from the end if it exists
  let workingConnectionString = mssqlConnectionString;
  workingConnectionString = workingConnectionString.replace(trustCertPattern, '');

  // Split into parameters and filter out the standard ones
  const allParams = workingConnectionString
    .split(';')
    .map((param) => param.trim())
    .filter((param) => param.length > 0);

  const additionalParams = allParams.filter((param) => {
    const paramName = param.split('=')[0]?.trim().toLowerCase();
    return (
      paramName && !standardParams.includes(paramName) && paramName !== 'trust server certificate'
    );
  });

  if (additionalParams.length > 0) {
    additionalParameters = additionalParams.join('; ');
    if (additionalParams.length === 1) {
      additionalParameters += ';';
    }
  }

  return {
    server,
    port,
    database,
    user: userId,
    password,
    additionalParameters,
  };
}

export function buildConnectionString(
  params: ConnectionStringParams,
  showPassword: boolean = false,
): string {
  const baseConnectionString = `Server=${params.server ?? ''}${
    params.port ? `,${params.port}` : ''
  }; Database=${params.database ?? ''}; User Id=${params.user ?? ''}; Password=${
    showPassword ? (params.password ?? '') : maskedPassword
  }`;

  const additionalParams = params.additionalParameters?.trim();
  // Remove trailing semicolons and whitespace. This does not impact the backend.
  const normalizedAdditionalParams = additionalParams?.replace(/;+\s*$/, '');
  const connectionString = additionalParams
    ? `${baseConnectionString}; ${normalizedAdditionalParams}`
    : baseConnectionString;

  // Backend adds Trust Server Certificate=true; automatically; append only if not present already
  const hasTrust = /(?:^|;)\s*trust\s{1,3}server\s{1,3}certificate\s*=\s*true\s*(?:;|$)/i.test(
    connectionString,
  );
  return hasTrust ? connectionString : `${connectionString};Trust Server Certificate=true;`;
}

function validateAdditionalParameterField(input: string): string[] {
  const errorMessages: string[] = [];

  // Validate format: one or more key=value; pairs
  const whitespace = '\\s{0,10}';
  const keyPattern = '[^=\\s]{1,50}(?:\\s{1}[^=\\s]{1,50})*'; // Key with max 1 space between words
  const quotedValue = "'[^']{0,500}'";
  const unquotedValue = '[^;]{0,500}';
  const valuePattern = `(?:${quotedValue}|${unquotedValue})`;

  // Allowed values: https://learn.microsoft.com/en-us/dotnet/api/system.data.sqlclient.sqlconnection.connectionstring?view=net-9.0-pp
  const fullPattern = new RegExp(
    `^(${whitespace}(?<key>${keyPattern})${whitespace}=` +
      `${whitespace}(?<value>${valuePattern})${whitespace};${whitespace}){1,20}$`,
  );

  if (!fullPattern.test(input)) {
    errorMessages.push(t('Installation.Database.InvalidFormatMsg'));
  }

  // Split the additional parameters and check each one
  const params = input
    .split(';')
    .map((param) => param.trim())
    .filter((param) => param.length > 0);

  params.forEach((param) => {
    const paramName = param.split('=')[0]?.trim().toLowerCase();

    // Special handling for Trust Server Certificate: only show the dedicated message
    if (paramName?.includes('trust server certificate')) {
      errorMessages.push(t('Installation.Database.TrustServerCertificateMsg'));
      return; // avoid adding a generic forbidden message, too
    }

    if (paramName && forbiddenParams?.includes(paramName)) {
      errorMessages.push(
        t('Installation.Database.Parameter_paramName_IsNotAllowedMsg', {
          paramName: paramName,
        }),
      );
    }
  });

  return errorMessages;
}

/**
 * Masks or reveals the password within a raw MSSQL connection string without parsing
 * any other parameters. Keeps the original string intact except for the password value.
 * This is useful to render integrated connection strings while keeping params and
 * connection-string flows independent.
 */
export function renderMaskedConnectionString(
  connectionString: string | undefined,
  showPassword: boolean = false,
): string {
  if (!connectionString) return '';
  if (showPassword) return connectionString;

  // Replace password value (Password=... or Pwd=...), quoted or unquoted, case-insensitive
  const passwordPair = /(password|pwd)\s*=\s*(?:'[^']*'|[^;]*)/gi;
  return connectionString.replace(passwordPair, (match, key: string) => {
    // Preserve the original key casing
    return `${key}=${maskedPassword}`;
  });
}

/**
 * Creates initial form values based on installation config and database type
 */
export function createInitialValues(
  installationConfig: InstallationConfigDto,
  databaseType: 'integrated' | 'custom',
  withoutConnectionStrings?: boolean,
  isFirstTimeInstallation?: boolean,
): PartialDeep<UpdateConnectionStringsRequestDto> {
  let initialDefaultConnectionString: ConnectionStringParams;
  let initialBlobStoringConnectionString: ConnectionStringParams;
  let backupConfirmed = false;

  if (databaseType === 'custom') {
    initialDefaultConnectionString = getParametersFromConnectionString(
      installationConfig.defaultConnectionString,
    );
    initialBlobStoringConnectionString = getParametersFromConnectionString(
      installationConfig.blobStoringConnectionString,
    );
    if (isFirstTimeInstallation) {
      backupConfirmed = true;
    }
  } else {
    initialDefaultConnectionString = getParametersFromConnectionString(
      installationConfig.integratedDefaultConnectionString,
    );
    initialBlobStoringConnectionString = getParametersFromConnectionString(
      installationConfig.integratedBlobConnectionString,
    );
    backupConfirmed = true;
  }

  return {
    defaultConnectionString: withoutConnectionStrings ? {} : initialDefaultConnectionString,
    blobStoringConnectionString: withoutConnectionStrings ? {} : initialBlobStoringConnectionString,
    backupConfirmed,
    $type:
      UpdateConnectionStringsRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedInstallDtoUpdateConnectionStringsRequestDtoPtwPtw100AppZeroApplicationShared,
  };
}
/**
 * Creates a custom validation function for additional parameters in database connection strings
 */
export function createDatabaseValidation(): CustomValidateFunction<UpdateConnectionStringsRequestDto> {
  return (data) => {
    const errors: AggregatedAjvError[] = [];

    if (data.defaultConnectionString?.additionalParameters) {
      const errorMessages = validateAdditionalParameterField(
        data.defaultConnectionString?.additionalParameters,
      );
      if (errorMessages.length > 0) {
        errors.push({
          path: 'defaultConnectionString.additionalParameters',
          errors: errorMessages,
        });
      }
    }

    if (data.blobStoringConnectionString?.additionalParameters) {
      const errorMessages = validateAdditionalParameterField(
        data.blobStoringConnectionString?.additionalParameters,
      );
      if (errorMessages.length > 0) {
        errors.push({
          path: 'blobStoringConnectionString.additionalParameters',
          errors: errorMessages,
        });
      }
    }

    if (!data.backupConfirmed) {
      errors.push({
        path: 'backupConfirmed',
        errors: [t('ThisConfirmationIsRequired')],
      });
    }

    return errors;
  };
}
